import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  ButtonGroup,
  Flex,
  FormField,
  Heading,
  Input,
  Label,
  LoadingButton,
  ModalDialog,
  Select,
  spaceMixin,
  Textarea,
  TextButton,
  Toast,
  token,
  useToast,
} from '@zillow/constellation';
import {
  DatarouterError,
  stringHelper,
  useRentalManagerMaintenanceConfigWebApiHandler_config,
  useRentalManagerUser,
} from '@zg-rentals/rental-manager-env';

import { useMaintenanceTrackerTaskUserWebApiHandler_create } from '@zg-rentals/ts-maintenance-tracker-client-react-query';

import { emitCloseTaskWithoutMakingTaskEvent, emitCreateNewTaskEvent } from '../clickstream/CreateNewTaskEvent';

const CreateTaskSchema = Yup.object().shape({
  name: Yup.string().max(100).required('A name is required to create a task'),
  category: Yup.string(),
  isMultifamily: Yup.boolean(),
  listingAlias: Yup.string().required('A property is required to create a task'),
  unit: Yup.string().when('isMultifamily', { is: true, then: Yup.string().required('Select unit.') }),
  note: Yup.string().max(150),
});

import { USE_TASKS_LIST } from '../constants/TaskConstants';
import PropertyField from '../components/PropertyField';
import { CharacterCounter } from '../components/CharacterCounter';
import FormikErrorMessage from '../components/FormikErrorMessage';

import type { ListingAlias, MaintenanceTaskCategory } from '@zg-rentals/ts-maintenance-tracker-client';

export type CreateFormFields = {
  category: string;
  cost?: number;
  note: string;
  feedModelId: string;
  isMultifamily: boolean;
  listingAlias: string;
  property?: string;
  name: string;
  unit?: string;
  vendor?: string;
};

const initialValues: CreateFormFields = {
  category: '',
  note: '',
  isMultifamily: false,
  listingAlias: '',
  property: '',
  name: '',
  unit: '',
  feedModelId: '',
};

type Props = {
  onClose: (newTaskId?: string) => void;
  listingAlias?: string;
};

export const StyledHR = styled.div`
  flex-grow: 0;
  margin-bottom: ${spaceMixin('sm')}px;
  border-bottom: 1px solid ${token('colors.gray300')};
  border-right: none;
  margin-left: -${spaceMixin('sm')}px;
  margin-right: -${spaceMixin('sm')}px;
`;
export const CreateNewTaskModal = ({ onClose, listingAlias }: Props) => {
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();
  const { user } = useRentalManagerUser();

  const [nameCharCount, setNameCharCount] = useState(0);
  const [noteCharCount, setNoteCharCount] = useState(0);

  const { mutate: createTask } = useMaintenanceTrackerTaskUserWebApiHandler_create();
  const { data: categoryOptions } = useRentalManagerMaintenanceConfigWebApiHandler_config({ staleTime: Infinity });

  const handleClosingWithoutMakingTask = () => {
    emitCloseTaskWithoutMakingTaskEvent({ user });
    onClose('');
  };

  return (
    <ModalDialog
      onClose={handleClosingWithoutMakingTask}
      size={{ default: 'fullScreen', md: 'sm' }}
      isOpen={true}
      renderHeader={<Heading level={1}>Create a task</Heading>}
      renderBody={
        <Formik
          initialValues={initialValues}
          validationSchema={CreateTaskSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            createTask(
              {
                postData: {
                  // format to be UNDER_SCORE_ALL_CAPS
                  // NOTE: `category` fallback `null` is needed https://zillowgroup.atlassian.net/browse/ZRM-14520
                  category:
                    (stringHelper.camelCaseToUnderScoreAllCaps(values.category) as MaintenanceTaskCategory) || null,
                  ...(values.feedModelId && { feedModelId: values.feedModelId }),
                  // double cast is necessary because TypeScript does not allow direct casting from a union type
                  listingAlias: (listingAlias ?? values.listingAlias) as unknown as ListingAlias,
                  ...(values.note && { note: values.note }),
                  name: values.name,
                },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: [USE_TASKS_LIST] });
                  resetForm();
                  emitCreateNewTaskEvent({ user });
                },
                onError: (error) => {
                  enqueueToast(
                    <Toast
                      appearance="error"
                      body={DatarouterError.getDisplayMessage(
                        error,
                        'Error occurred with saving your maintenance task.',
                      )}
                    />,
                  );
                },
                onSettled: (data) => {
                  onClose(data?.maintenanceTaskId);
                  setSubmitting(false);
                },
              },
            );
          }}
        >
          {({ isValid, isSubmitting, dirty, setFieldValue, setFieldTouched, errors, values, touched }) => (
            <Form placeholder="">
              <FormField marginBottom="md">
                <Label required>Task name</Label>
                <Field
                  as={Input}
                  name="name"
                  placeholder="Example: Water heater repair"
                  error={!!errors.name && touched.name}
                  value={values.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (listingAlias) {
                      setFieldValue('listingAlias', listingAlias);
                    }

                    setFieldValue('name', e.target.value);
                    setNameCharCount(e.target.value.length);

                    setTimeout(() => setFieldTouched('name', true));
                  }}
                />
                {errors.name && touched.name ? (
                  <FormikErrorMessage name="name" />
                ) : (
                  <CharacterCounter numerator={nameCharCount} denominator={100} />
                )}
              </FormField>
              {!listingAlias && <PropertyField />}
              <FormField marginTop="md">
                <Label>Category</Label>
                <Field
                  as={Select}
                  name="category"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('category', e.target.value);
                  }}
                >
                  <option value=""></option>
                  {categoryOptions &&
                    categoryOptions.categories.map((option) => (
                      <option key={option.persistentString} value={option.persistentString}>
                        {option.display}
                      </option>
                    ))}
                </Field>
              </FormField>
              <FormField marginTop="md" marginBottom="lg">
                <Label>Notes</Label>
                <Field
                  as={Textarea}
                  placeholder="Example: Tenant called this morning, has no hot water "
                  name="note"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('note', e.target.value, true);
                    setNoteCharCount(e.target.value.length);
                  }}
                />
                <CharacterCounter numerator={noteCharCount} denominator={150} />
                <FormikErrorMessage name="note" />
              </FormField>
              <StyledHR />
              <Flex display="flex" justifyContent="flex-end" marginTop="sm">
                <ButtonGroup aria-label="Cancel or Submit">
                  <TextButton type="button" onClick={handleClosingWithoutMakingTask}>
                    Cancel
                  </TextButton>
                  <LoadingButton
                    loading={isSubmitting}
                    disabled={!dirty || !isValid}
                    buttonType="primary"
                    type="submit"
                  >
                    Create
                  </LoadingButton>
                </ButtonGroup>
              </Flex>
            </Form>
          )}
        </Formik>
      }
    />
  );
};
